import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ZapIcon, RocketIcon, CrownIcon } from 'lucide-react';
const plans = [{
  name: 'Starter',
  icon: ZapIcon,
  price: '2.9%',
  description: 'Perfect for small nonprofits just getting started',
  features: ['Accept all major cards', 'Instant payouts', 'Basic analytics', 'Email support', 'Mobile optimized', 'Recurring donations'],
  gradient: 'from-blue-500 to-cyan-500',
  popular: false
}, {
  name: 'Professional',
  icon: RocketIcon,
  price: '2.5%',
  description: 'Most popular for growing organizations',
  features: ['Everything in Starter', 'Advanced analytics', 'Custom branding', 'Priority support', 'Donor management', 'Campaign tracking', 'API access', 'Webhooks'],
  gradient: 'from-orange-500 to-pink-500',
  popular: true
}, {
  name: 'Enterprise',
  icon: CrownIcon,
  price: 'Custom',
  description: 'For large organizations with complex needs',
  features: ['Everything in Professional', 'Dedicated account manager', '24/7 phone support', 'Custom integrations', 'White-label options', 'Volume discounts', 'SLA guarantee', 'Custom contracts'],
  gradient: 'from-purple-500 to-indigo-500',
  popular: false
}];
export function PricingSection() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  return <section className="py-24 lg:py-32 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden" data-id="element-227">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]" data-id="element-228">
        <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)`,
        backgroundSize: '48px 48px'
      }} data-id="element-229" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8" data-id="element-230">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-231">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-6" data-id="element-232">
            <span className="text-sm font-semibold text-orange-600" data-id="element-233">
              Simple, transparent pricing
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4" data-id="element-234">
            Pay only for what you use
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8" data-id="element-235">
            No setup fees. No monthly fees. No hidden costs. Just a simple
            percentage per transaction.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 p-1 bg-slate-200 rounded-full" data-id="element-236">
            <button onClick={() => setBillingCycle('monthly')} className={`px-6 py-2 rounded-full font-medium transition-all ${billingCycle === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-237">
              Per Transaction
            </button>
            <button onClick={() => setBillingCycle('annual')} className={`px-6 py-2 rounded-full font-medium transition-all ${billingCycle === 'annual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-238">
              Volume Discounts
            </button>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-239">
          {plans.map((plan, index) => <motion.div key={plan.name} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className={`relative bg-white rounded-3xl p-8 border-2 ${plan.popular ? 'border-orange-500 shadow-2xl shadow-orange-500/20 scale-105' : 'border-slate-200 hover:border-slate-300'} transition-all`} data-id="element-240">
              {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-sm font-semibold rounded-full" data-id="element-241">
                  Most Popular
                </div>}

              <div className={`inline-flex p-3 rounded-2xl bg-gradient-to-br ${plan.gradient} mb-6`} data-id="element-242">
                <plan.icon className="w-6 h-6 text-white" data-id="element-243" />
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-244">
                {plan.name}
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-245">{plan.description}</p>

              <div className="mb-6" data-id="element-246">
                <div className="flex items-baseline gap-2" data-id="element-247">
                  <span className="text-5xl font-bold text-slate-900" data-id="element-248">
                    {plan.price}
                  </span>
                  {plan.price !== 'Custom' && <span className="text-slate-600" data-id="element-249">+ $0.30</span>}
                </div>
                <p className="text-sm text-slate-500 mt-1" data-id="element-250">
                  {plan.price === 'Custom' ? 'Contact us for pricing' : 'per successful transaction'}
                </p>
              </div>

              <button className={`w-full py-3 rounded-full font-semibold transition-all mb-8 ${plan.popular ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:shadow-lg hover:shadow-orange-500/30' : 'bg-slate-100 text-slate-900 hover:bg-slate-200'}`} data-id="element-251">
                {plan.price === 'Custom' ? 'Contact Sales' : 'Start Free Trial'}
              </button>

              <div className="space-y-3" data-id="element-252">
                {plan.features.map(feature => <div key={feature} className="flex items-start gap-3" data-id="element-253">
                    <div className={`flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br ${plan.gradient} flex items-center justify-center mt-0.5`} data-id="element-254">
                      <CheckIcon className="w-3 h-3 text-white" data-id="element-255" />
                    </div>
                    <span className="text-slate-700" data-id="element-256">{feature}</span>
                  </div>)}
              </div>
            </motion.div>)}
        </div>

        {/* Additional Info */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mt-16 text-center" data-id="element-257">
          <p className="text-slate-600 mb-4" data-id="element-258">
            All plans include bank-level security, PCI compliance, and fraud
            protection
          </p>
          <a href="#" className="text-orange-600 font-semibold hover:text-orange-700 transition-colors" data-id="element-259">
            Compare all features →
          </a>
        </motion.div>
      </div>
    </section>;
}